// The dashboard's two passing messages: an undo, and a flash.
//
// Closing a shop, removing a cashier and revoking a till are undone, not
// confirmed: the row leaves at once, a ten-second Undo shows, and the server
// is told only when the ten seconds run out. Undo puts the row back and
// the server never hears of it. A second removal while one is waiting sends
// the first straight away, so two can never be half done at once.
//
// A flash says something saved. Both are drawn once, by the dashboard shell.

import { holdReloadWhile } from '../build.svelte';

interface Waiting {
	text: string;
	commit: () => Promise<void>;
	restore: () => void;
}

let waiting = $state<Waiting | null>(null);
let flashText = $state('');

// A newer bundle loads at the next navigation (INT-7), but never over a
// removal still waiting for its Undo: the server has not been told of it, and
// a new page would forget it.
holdReloadWhile(() => waiting !== null);

export function pendingUndo(): { text: string } | null {
	return waiting;
}

export function currentFlash(): string {
	return flashText;
}

/** Take something away now, and tell the server when the undo runs out. */
export function removeWithUndo(
	text: string,
	commit: () => Promise<void>,
	restore: () => void,
): void {
	void commitNow();
	waiting = { text, commit, restore };
}

/** The Undo was tapped: put it back and tell nobody. */
export function undo(): void {
	const current = waiting;
	waiting = null;
	current?.restore();
}

/** The ten seconds ran out, or another removal came along. */
export async function commitNow(): Promise<void> {
	const current = waiting;
	waiting = null;
	if (current) await current.commit();
}

export function flash(text: string): void {
	flashText = text;
}

export function clearFlash(): void {
	flashText = '';
}
